# SideBar.vue

<template>
  <div v-if="isLoggedIn">
    <!-- User Profile Section -->
    <v-list-item class="grey lighten-4">
      <v-menu
          v-model="showProfile"
          :close-on-content-click="false"
          offset-y
          max-width="300"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-list-item
              v-bind="attrs"
              v-on="on"
              link
          >
            <v-list-item-avatar color="primary">
              <v-img
                  v-if="userInfo.avatarUrl"
                  :src="userInfo.avatarUrl"
              ></v-img>
              <span v-else class="white--text">
                {{ userInfo.username?.[0]?.toUpperCase() }}
              </span>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="subtitle-2">
                {{ userName }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                {{ userInfo.email }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>

        <!-- Profile Menu Content -->
        <v-card>
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <a
                      :href="gitlabUserUrl"
                      target="_blank"
                      class="text-decoration-none"
                      v-if="userInfo.username"
                  >
                    {{ userInfo.name || userInfo.username }}
                    <v-icon small class="ml-1">mdi-open-in-new</v-icon>
                  </a>
                  <span v-else>{{ userInfo.name || userInfo.username }}</span>
                </v-list-item-title>

                <v-list-item-subtitle>
                  <a
                      :href='`mailto:${userInfo.email}`'
                      class="text-decoration-none"
                  >
                    {{ userInfo.email }}
                  </a>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-divider></v-divider>

            <!-- Quick Actions -->
            <v-list dense>
              <v-list-item @click="refreshUserInfo" :disabled="isRefreshing">
                <v-list-item-icon>
                  <v-icon small>mdi-refresh</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Refresh Profile</v-list-item-title>
                <v-list-item-action>
                  <v-progress-circular
                      v-if="isRefreshing"
                      indeterminate
                      size="16"
                      color="primary"
                  ></v-progress-circular>
                </v-list-item-action>
              </v-list-item>

              <v-list-item @click="logout" link>
                <v-list-item-icon>
                  <v-icon small color="error">mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="error--text">Logout</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-list>
        </v-card>
      </v-menu>
    </v-list-item>

    <v-divider></v-divider>

    <!-- Navigation Menu -->
    <v-list dense nav class="py-0">
      <!-- Common Navigation -->
      <template v-for="(item, i) in commonNavItems">
        <v-list-item
            :key="i"
            :to="item.to"
            exact
            link
            class="navigation-item"
        >
          <v-list-item-icon>
            <v-icon small>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="caption">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <!-- Role-based Navigation -->
      <template v-for="(section, i) in filteredNavigation">
        <v-subheader
            :key="'header-' + i"
            class="mt-3 caption grey--text text--darken-1"
        >
          {{ section.title }}
        </v-subheader>

        <template v-for="(group, j) in section.items">
          <v-list-group
              v-if="group.items"
              :key="'group-' + i + '-' + j"
              :prepend-icon="group.icon"
              no-action
              class="navigation-group"
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title class="caption">
                  {{ group.title }}
                </v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item
                v-for="(subItem, k) in group.items"
                :key="'subitem-' + i + '-' + j + '-' + k"
                :to="subItem.to"
                exact
                link
                class="navigation-item pl-4"
            >
              <v-list-item-title class="caption">
                {{ subItem.title }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>

          <v-list-item
              v-else
              :key="'item-' + i + '-' + j"
              :to="group.to"
              exact
              link
              class="navigation-item"
          >
            <v-list-item-icon>
              <v-icon small>{{ group.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="caption">
              {{ group.title }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </template>
    </v-list>
  </div>
</template>




<script>
import { mapGetters } from 'vuex'
import userApi from "@/config/api/users"
import oauthApi from '@/config/api/oauth'

export default {
  name: "SideBar",

  data: () => ({
    showProfile: false,
    isRefreshing: false,
    gitlabBaseUrl: 'https://gitlab.innilabs.co.id',
    commonNavItems: [
      {
        title: 'Dashboard',
        to: '/dashboard',
        icon: 'mdi-view-dashboard'
      },
      {
        title: 'Projects',
        to: '/project',
        icon: 'mdi-folder-multiple'
      }
    ],
    navigationConfig: [
      {
        title: 'USER NAVIGATION',
        role: 'USER',
        items: [
          {
            title: 'Configuration Management',
            icon: 'mdi-cog',
            items: [
              {
                title: 'Merge Request',
                to: '/req/merge'
              }
            ]
          },
          {
            title: 'Basic Setup',
            icon: 'mdi-tune',
            items: [
              {
                title: 'My Approval',
                to: '/my-aprv'
              }
            ]
          }
        ]
      },
      {
        title: 'APPROVAL NAVIGATION',
        role: 'APPROVER',
        items: [
          {
            title: 'Deployment Management',
            icon: 'mdi-rocket-launch',
            items: [
              {
                title: 'Prepared Deployments',
                to: '/deploy/prepare'
              },
              {
                title: 'Deployment History',
                to: '/deploy/history'
              },
              {
                title: 'Merge Request',
                to: '/req/merge'
              }
            ]
          },
          {
            title: 'Setup Management',
            icon: 'mdi-cog-outline',
            items: [
              {
                title: 'Absence',
                to: '/absence'
              },
              {
                title: 'My Approval',
                to: '/my-aprv'
              }
            ]
          }
        ]
      },
      {
        title: 'ADMIN NAVIGATION',
        role: 'ADMIN',
        items: [
          {
            title: 'User Management',
            icon: 'mdi-account-cog',
            items: [
              {
                title: 'User Role',
                to: '/mgmt/user'
              }
            ]
          }
        ]
      }
    ]
  }),

  computed: {
    gitlabUserUrl() {
      return this.userInfo.username
          ? `${this.gitlabBaseUrl}/${this.userInfo.username}`
          : null
    },
    ...mapGetters([
      'isLogged',
      'getUser',
      'getUserName',
      'getRoles',
      'isAdmin',
      'isUser'
    ]),

    userInfo() {
      return this.getUser || {}
    },

    userName() {
      return this.getUserName || 'Not logged in'
    },

    isLoggedIn() {
      return this.isLogged
    },

    userRoles() {
      return this.getRoles || []
    },

    filteredNavigation() {
      return this.navigationConfig.filter(section => this.sectionAllowed(section.role))
    }
  },

  methods: {
    // 3단계 롤 기준 nav 노출. ADMIN 섹션은 admin 만, USER 섹션은 user 이상. 레거시(APPROVER 등)는 숨김.
    sectionAllowed(role) {
      if (role === 'ADMIN') return this.isAdmin
      if (role === 'USER') return this.isUser
      return false
    },
    getRoleColor(role) {
      const colors = {
        ADMIN: 'error',
        APPROVER: 'success',
        USER: 'primary'
      }
      return colors[role] || 'grey'
    },

    async refreshUserInfo() {
      this.isRefreshing = true
      try {
        const user = await userApi.updateUserInfo()
        this.$store.commit("setUser", user)
      } catch (error) {
        this.showErrorSnack('Failed to refresh user info')
        console.error('Error refreshing user info:', error)
      } finally {
        this.isRefreshing = false
      }
    },

    async logout() {
      this.showProfile = false
      try {
        await oauthApi.logout()
      } catch (error) {
        console.error('Error during logout:', error)
      } finally {
        this.$store.commit("removeUser")
        this.$router.push("/login")
      }
    },

    showErrorSnack(message) {
      this.$snotify.error(message, {
        timeout: 3000,
        showProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true
      })
    }
  }
}
</script>
<style scoped>
.navigation-item {
  min-height: 40px !important;
}

.navigation-item.v-list-item--active {
  background-color: #F5F7FA !important;
  width: 100%;
}

.navigation-item.v-list-item--active::before {
  opacity: 0;
}

.navigation-item.v-list-item--active .v-icon,
.navigation-item.v-list-item--active .v-list-item__title {
  color: #7986CB !important;
}

.navigation-group.v-list-group--active > .v-list-group__header .v-list-item__title {
  color: #7986CB !important;
}
</style>